export const COPILOT_ACTIVITY_LOG_PATH = '/workspace/activity-log.md';

export type CopilotActivityLogKind = 'tool' | 'run' | 'auth';

export type CopilotActivityLogEntry = {
  timestamp: string;
  actor: string;
  kind: CopilotActivityLogKind;
  name: string;
  status: string;
  durationMs?: number;
  summary: string;
  artifactPath?: string;
  raw: string;
};

const FIELD_SEPARATOR = ' | ';
const EMPTY_FIELD = '-';
const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T[^\s]+Z/;
const DURATION_PATTERN = /^(\d+)ms$/;
const LEGACY_ACTOR_PATTERN = /^\[([^\]]+)\]/;
const LEGACY_DURATION_PATTERN = /\b(\d+)ms\b/;
const LEGACY_ARTIFACT_PATTERN = /->\s+(\/\S+)/;

const sanitizeField = (value: string): string => {
  const collapsed = value.replace(/\s+/g, ' ').trim();

  if (collapsed.length === 0) {
    return EMPTY_FIELD;
  }

  return collapsed.replaceAll(FIELD_SEPARATOR, ' / ');
};

const displayField = (value: string | undefined): string => {
  if (!value || value === EMPTY_FIELD) {
    return EMPTY_FIELD;
  }

  return sanitizeField(value);
};

const parseOptionalField = (value: string): string | undefined => {
  if (!value || value === EMPTY_FIELD) {
    return undefined;
  }

  return value;
};

const parseDurationField = (value: string): number | undefined => {
  const match = value.match(DURATION_PATTERN);

  if (!match) {
    return undefined;
  }

  return Number.parseInt(match[1], 10);
};

export const formatCopilotActivityLogLine = ({
  timestamp = new Date().toISOString(),
  actor,
  kind,
  name,
  status,
  durationMs,
  summary,
  artifactPath,
}: {
  timestamp?: string;
  actor: string;
  kind: CopilotActivityLogKind;
  name: string;
  status: string;
  durationMs?: number;
  summary?: string;
  artifactPath?: string;
}): string =>
  [
    timestamp,
    sanitizeField(actor),
    kind,
    sanitizeField(name),
    sanitizeField(status),
    typeof durationMs === 'number' ? `${durationMs}ms` : EMPTY_FIELD,
    displayField(summary),
    displayField(artifactPath),
  ].join(FIELD_SEPARATOR);

const parseDelimitedLine = (line: string): CopilotActivityLogEntry | null => {
  const parts = line.split(FIELD_SEPARATOR);

  if (parts.length !== 8) {
    return null;
  }

  const [timestamp, actor, kind, name, status, duration, summary, artifact] =
    parts;

  if (!ISO_TIMESTAMP_PATTERN.test(timestamp)) {
    return null;
  }

  if (kind !== 'tool' && kind !== 'run' && kind !== 'auth') {
    return null;
  }

  return {
    timestamp,
    actor,
    kind,
    name,
    status,
    durationMs: parseDurationField(duration),
    summary: parseOptionalField(summary) ?? '',
    artifactPath: parseOptionalField(artifact),
    raw: line,
  };
};

const parseLegacyLine = (line: string): CopilotActivityLogEntry => {
  const timestampMatch = line.match(ISO_TIMESTAMP_PATTERN);
  const timestamp = timestampMatch ? timestampMatch[0] : '';
  const rest = timestamp ? line.slice(timestamp.length).trimStart() : line;
  const actorMatch = rest.match(LEGACY_ACTOR_PATTERN);
  const actor = actorMatch ? actorMatch[1] : 'main';
  const afterActor = actorMatch
    ? rest.slice(actorMatch[0].length).trimStart()
    : rest;
  const durationMatch = afterActor.match(LEGACY_DURATION_PATTERN);
  const artifactMatch = afterActor.match(LEGACY_ARTIFACT_PATTERN);

  if (afterActor.startsWith('auth ')) {
    const applied = afterActor.startsWith('auth applied');

    return {
      timestamp,
      actor,
      kind: 'auth',
      name: applied ? 'applied' : 'not-applied',
      status: applied ? 'success' : 'error',
      durationMs: durationMatch
        ? Number.parseInt(durationMatch[1], 10)
        : undefined,
      summary: afterActor.replace(/^auth\s+(applied|not-applied)\s*/, ''),
      artifactPath: artifactMatch?.[1],
      raw: line,
    };
  }

  if (
    afterActor.startsWith('> subagent:') ||
    afterActor.startsWith('< subagent:')
  ) {
    const phase = afterActor.startsWith('>') ? 'start' : 'end';
    const typeMatch = afterActor.match(/subagent:(\S+)/);

    return {
      timestamp,
      actor,
      kind: 'run',
      name: typeMatch?.[1] ?? 'sub-agent',
      status: phase,
      durationMs: durationMatch
        ? Number.parseInt(durationMatch[1], 10)
        : undefined,
      summary: afterActor,
      artifactPath: artifactMatch?.[1],
      raw: line,
    };
  }

  const [toolName = 'unknown', status = 'success', ...summaryParts] =
    afterActor.split(/\s+/);

  return {
    timestamp,
    actor,
    kind: 'tool',
    name: toolName,
    status,
    durationMs: durationMatch
      ? Number.parseInt(durationMatch[1], 10)
      : undefined,
    summary: summaryParts
      .filter((part) => !DURATION_PATTERN.test(part) && !part.startsWith('->'))
      .join(' '),
    artifactPath: artifactMatch?.[1],
    raw: line,
  };
};

export const parseCopilotActivityLogLine = (
  line: string,
): CopilotActivityLogEntry => {
  const trimmed = line.trim();

  return parseDelimitedLine(trimmed) ?? parseLegacyLine(trimmed);
};

export const parseCopilotActivityLog = (
  content: string,
): CopilotActivityLogEntry[] =>
  content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map(parseCopilotActivityLogLine);
